import * as LDClient from 'launchdarkly-js-client-sdk';
import { env } from '@/env';

/**
 * Supported feature-flag value types.
 */
export type FlagValue = boolean | string | number;

/**
 * Per-flag bookkeeping. The presence of an entry in the `flags` map doubles as
 * the "this key has been requested" guard — mirroring the Angular service's
 * `Record<flagKey, Signal>` — so a LaunchDarkly `change:` listener is only ever
 * registered once per key, and only after that key has actually been hit.
 */
interface FlagEntry {
  value: FlagValue;
  defaultValue: FlagValue;
  /** React `useSyncExternalStore` callbacks to notify on change. */
  subscribers: Set<() => void>;
  /** Whether the LaunchDarkly `change:<key>` listener has been wired up. */
  listenerRegistered: boolean;
}

/**
 * Framework-agnostic singleton wrapper around the LaunchDarkly browser SDK.
 *
 * Design goals (ported from the Angular `FeatureFlagService`):
 *  - Lazy listeners: a `change:<key>` listener is registered only when a flag
 *    key is first requested somewhere in the app. Keys that are never read get
 *    no listener at all. From the moment a key is hit, changes are captured.
 *  - Pre-init safety: flags requested before the client finishes initializing
 *    are recorded and "activated" (value read + listener wired) once init
 *    completes, so ordering between component mount and auth/init doesn't matter.
 *  - React binding: exposes `subscribe`/`getSnapshot` for `useSyncExternalStore`.
 */
class FeatureFlagClient {
  private client?: LDClient.LDClient;
  private ready = false;
  private readonly flags = new Map<string, FlagEntry>();

  /**
   * Initialize the LaunchDarkly client with the given context, or — if already
   * initialized — re-identify against the new context (e.g. on tenant switch).
   */
  public async initialize(context: LDClient.LDContext): Promise<void> {
    if (this.client) {
      await this.identify(context);
      return;
    }

    const sdkKey = env.LAUNCHDARKLY_SDK_KEY;
    if (!sdkKey) {
      console.warn(
        '[feature-flags] No LaunchDarkly SDK key configured; flags will resolve to their defaults.',
      );
      return;
    }

    this.client = LDClient.initialize(sdkKey, context, {
      application: { id: 'nvx-web-app', version: env.APP_VERSION },
    });

    this.registerGlobalListeners();

    try {
      await this.client.waitForInitialization(5);
    } catch (error) {
      // Non-fatal: flags fall back to their last-known/default values.
      console.warn('[feature-flags] LaunchDarkly did not initialize within timeout', error);
    }

    this.ready = true;

    // Activate any flags that were requested before init completed.
    for (const [key, entry] of this.flags) {
      this.activateFlag(key, entry);
    }
  }

  /**
   * Re-identify the current client with a new context. LaunchDarkly emits
   * `change:<key>` events for any flags whose value changed, which the
   * per-key listeners already handle.
   */
  public async identify(context: LDClient.LDContext): Promise<void> {
    if (!this.client) return;
    try {
      await this.client.identify(context);
    } catch (error) {
      console.warn('[feature-flags] LaunchDarkly identify failed', error);
    }
  }

  /**
   * Imperative read for use outside React (route loaders, guards). Counts as a
   * "hit" and lazily registers a listener for the key, just like the hook.
   */
  public getFlag<T extends FlagValue>(key: string, defaultValue: T): T {
    return this.ensureFlag(key, defaultValue) as T;
  }

  /**
   * Subscribe a `useSyncExternalStore` callback to a flag. The act of
   * subscribing is the "key hit" that lazily activates the flag.
   */
  public subscribe(key: string, defaultValue: FlagValue, callback: () => void): () => void {
    this.ensureFlag(key, defaultValue);
    const entry = this.flags.get(key)!;
    entry.subscribers.add(callback);
    return () => {
      entry.subscribers.delete(callback);
    };
  }

  /**
   * Current cached value for a flag (synchronous snapshot for React).
   */
  public getSnapshot(key: string, defaultValue: FlagValue): FlagValue {
    return this.flags.get(key)?.value ?? defaultValue;
  }

  /**
   * Tear down the client and reset all flag state (on logout).
   */
  public async close(): Promise<void> {
    if (!this.client) return;
    try {
      await this.client.close();
    } catch {
      // Ignore teardown errors.
    }
    this.client = undefined;
    this.ready = false;
    this.flags.clear();
  }

  /**
   * Ensure a flag entry exists, creating it (and activating it if the client is
   * ready) on first request. Returns the current value.
   */
  private ensureFlag(key: string, defaultValue: FlagValue): FlagValue {
    let entry = this.flags.get(key);
    if (!entry) {
      entry = {
        value: defaultValue,
        defaultValue,
        subscribers: new Set(),
        listenerRegistered: false,
      };
      this.flags.set(key, entry);
      if (this.ready) {
        this.activateFlag(key, entry);
      }
    }
    return entry.value;
  }

  /**
   * Read the flag's current value from LaunchDarkly and register a single
   * `change:<key>` listener for it. Idempotent per key.
   */
  private activateFlag(key: string, entry: FlagEntry): void {
    if (!this.client || entry.listenerRegistered) return;

    entry.listenerRegistered = true;
    entry.value = (this.client.variation(key, entry.defaultValue) ?? entry.defaultValue) as FlagValue;

    this.client.on(`change:${key}`, (value: FlagValue) => {
      const next = (value ?? entry.defaultValue) as FlagValue;
      if (entry.value !== next) {
        entry.value = next;
        entry.subscribers.forEach((cb) => cb());
      }
    });

    // The freshly-read value may differ from the seeded default.
    entry.subscribers.forEach((cb) => cb());
  }

  /**
   * Connection/lifecycle listeners — registered once at init, not per flag.
   */
  private registerGlobalListeners(): void {
    if (!this.client) return;
    this.client.on('error', (error: unknown) => {
      console.error('[feature-flags] LaunchDarkly client error', error);
    });
  }
}

/**
 * Application-wide singleton. Initialized by `FeatureFlagProvider`, read via the
 * `useFeatureFlag` family of hooks (or `getFlag` for non-React call sites).
 */
export const featureFlagClient = new FeatureFlagClient();
